import { useCallback, useState } from 'react'
import { decideClose } from './unsavedWork'

/**
 * The #274 rule, wired once (#309 review).
 *
 * "Don't throw away what someone typed" was decided in `unsavedWork.ts` and
 * then implemented in exactly one panel. The same editor now mounts on four
 * surfaces — the failure slideout, the VSP and EyeMed slideouts, and the
 * Recovery drawer — and the other three closed straight through `onClose`, so
 * clicking the overlay mid-edit discarded the correction silently. One
 * component, four surfaces, one behaviour: that only holds if the behaviour
 * lives somewhere all four can share.
 *
 * Returns the guarded close every path (Escape, overlay, X) should call, the
 * dirty reporter to hand the editor, and whether the inline warning is showing.
 */
export function useUnsavedGuard(onClose: () => void): {
  guardedClose: () => void
  /**
   * The same warn-once rule for ANY action that leaves the panel — an in-app
   * navigation ("View check →"), a mutation that unmounts it ("Mark handled").
   * A hook only covers the paths that call it (#318 review): the overlay, the
   * X and Escape went through `guardedClose` while navigation unmounted the
   * panel straight past the typed work, and the most likely click after
   * reading a wrong value was the one that ate the correction.
   */
  guardAction: (action: () => void) => void
  /**
   * The same decision, but REPORTED rather than swallowed — for close paths
   * that have to undo themselves when refused.
   *
   * Browser Back is the one that needs this: the history entry is already
   * consumed by the time the handler runs, so refusing has to tell the sentinel
   * to re-arm (#274's third close path, the one that was never wired).
   */
  attemptClose: () => 'closed' | 'refused'
  setDirty: (dirty: boolean) => void
  dirty: boolean
  /** True when the operator has been warned and the edits are still there —
   *  render the inline bar. Deliberately not a modal: the operator is mid-task
   *  and a modal steals focus from the fields they are looking at. */
  showWarning: boolean
} {
  const [dirty, setDirtyState] = useState(false)
  const [warned, setWarned] = useState(false)

  const guardAction = useCallback((action: () => void) => {
    if (decideClose(dirty, warned) === 'confirm') {
      setWarned(true)
      return
    }
    action()
  }, [dirty, warned])

  const guardedClose = useCallback(
    () => guardAction(onClose),
    [guardAction, onClose],
  )

  const attemptClose = useCallback((): 'closed' | 'refused' => {
    if (decideClose(dirty, warned) === 'confirm') {
      setWarned(true)
      return 'refused'
    }
    onClose()
    return 'closed'
  }, [dirty, warned, onClose])

  // A cleared editor retracts the warning too — otherwise "you have unsaved
  // edits" would linger over a panel with nothing left to lose.
  const setDirty = useCallback((next: boolean) => {
    setDirtyState(next)
    if (!next) setWarned(false)
  }, [])

  return {
    guardedClose, guardAction, attemptClose, setDirty, dirty,
    showWarning: warned && dirty,
  }
}
